import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import {  IMetbe001, IRefModalItem } from "../interface";
import HierarchyObjetives from "./HierarchyObjetives";
import HierarchySWOT from "./HierarchySWOT";

const ModalItem: React.ForwardRefRenderFunction<IRefModalItem> = (_, ref) => {

	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const onCallbackFunction = useRef<Function | undefined>(undefined);

	const { translate} = useTranslation();
	const { confirm } = useConfirmModal();
	const { addToast } = RCCHookToast();
	const { 
		handleSubmit : handleSubmitAdd, 
		reset : resetAdd, 
		register: registerAdd,
		setValue: setValueAdd
	} = useForm();

	const [isObjective, setIsObjective] = useState(false);
	const [selectedGrouper, setSelectedGrouper] = useState<IMetbe001>({} as IMetbe001);
	
	const submitAddOrChange = async (data: any) => {

		const correctArray = Object.values(data).filter((x) => typeof x === "string").map((e) => typeof e === "string" ? parseInt(e, 10) : "");

		try{	
			if(isObjective){
				await RCCServicesApi.postRequest({
					method: "Strategies/InsertObjectives",
					body: {
						mea003Ids: correctArray,
						mee001Id: selectedGrouper.mee001Id
					}
				});
				if(onCallbackFunction.current)
					onCallbackFunction.current();
			}else{
				await RCCServicesApi.postRequest({
					method: "Strategies/InsertSwotItems",
					body: {
						mes002Ids: correctArray,
						mee001Id: selectedGrouper.mee001Id
					}
				});
				if(onCallbackFunction.current)
					onCallbackFunction.current();
			}

			addToast({
				color: "green",
				description: isObjective ? `${translate("Objetivo adicionada com sucesso")}` : `${translate("Item da SWOT adicionado com sucesso")}`,
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			standardErrorMessages(err, isObjective ? `${translate("Erro ao adicionar objetivo")}`:  `${translate("Erro ao adicionar item da SWOT")}`, addToast);
		}
	};

	const actionButtonAdd = async (objective: boolean, grouper: IMetbe001, onCallback?: Function) => {	
		setIsObjective(objective);
		setSelectedGrouper(grouper);
		resetAdd();
		onCallbackFunction.current = onCallback;
		modalAddOrChange.current?.openModal();
	};

	const actionButtonDelete = (objective: boolean, idItem: number, nameItem: string, aditionalParams: Object, onCallback?: Function) => {
		confirm(
			`${objective ? translate("Exclusão de Objetivo") : translate("Exclusão de Item de SWOT")}`, 
			`${objective ? `${translate("Deseja realmente excluir o objetivo")}: ${nameItem}` : `${translate("Deseja realmente excluir este item da SWOT")}: ${nameItem}`}`,
			async () => {


				if(objective){
					await RCCServicesApi.deleteRequest({
						method: "Strategies/DeleteObjective",
						urlParameters: {
							mee002Id: idItem,
							...aditionalParams
						}
					});
				}else{
					await RCCServicesApi.deleteRequest({
						method: "Strategies/DeleteSwotItem",
						urlParameters: {
							mes002Id: idItem,
							...aditionalParams
						}
					});	
				}
			
				addToast({
					color: "green",
					description:`${objective ? translate("Objetivo excluído com sucesso") : translate("Item da SWOT excluído com sucesso")}`,
					title: translate("Sucesso")
				});
		
				if(onCallback)
					onCallback();
			},
			(err) => {
				standardErrorMessages(err, objective ? `${translate("Erro ao excluir objetivo")}` : `${translate("Erro ao excluir item da SWOT")}`, addToast);
			},
			true
		);
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonDelete
	}));

	return (

		<>
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isObjective ? `${translate("Adicionar Objetivo")}` : `${translate("Adicionar Item da SWOT")}`,
					subtitle: `${translate("Estratégia")}: ${selectedGrouper.mee001Titulo}`,
					icon: {
						name: "plus"
					}
				}}
				styleInline={`${ModalSide}`}
				onSubmit={handleSubmitAdd(submitAddOrChange)}
			>
				<RCCGrid
					container
					item
				>
					{isObjective ? (
						<HierarchyObjetives
							mee001Id={selectedGrouper.mee001Id} 
							mea001Id={selectedGrouper.mee001Mea001Id}
							register={registerAdd} 
							setValue={setValueAdd}								
						/>
					) : (
						<HierarchySWOT 
							mee001Id={selectedGrouper.mee001Id} 
							register={registerAdd} 
							setValue={setValueAdd}								
						/>
					)}
				</RCCGrid>
			</Modal>
		</>

	);
};

export default forwardRef(ModalItem);
